/**
 * Scalping Pro Engine (High-Precision M1/M5 Scalper)
 * High-Precision Liquidity Hunt & Sweep Engine
 *
 * 1. Swings Pivot Detection: ta.pivothigh(len, len) and ta.pivotlow(len, len).
 * 2. Options:
 *    - 'Only Wicks': Liquidity sweeps through wicks, candle body closes back inside.
 *    - 'Only Outbreaks & Retest': Breakout with candle close, followed by pullback retest.
 *    - 'Wicks + Outbreaks & Retest': Combines both setups.
 * 3. Execution:
 *    - 'RETEST' (Default & High Win-Rate): Limit order placed exactly at the swept/broken pivot level.
 *    - 'MARKET': Immediate entry upon confirmation candle close.
 * 4. Sweep Area Boxes:
 *    - Dynamic boxes extended until broken or max bars reached.
 *    - Width 3 rejection wick line + dotted notch line.
 */

function getPipMultiplier(price) {
  if (price < 5.0) return 10000.0;
  if (price < 500.0) return 100.0;
  if (price < 15000.0) return 10.0;
  return 1.0;
}

function getPriceDecimals(price) {
  if (price < 5.0) return 5;
  if (price < 500.0) return 3;
  return 2;
}

function calcEma(arr, period) {
  const alpha = 2.0 / (period + 1.0);
  const ema = new Float64Array(arr.length);
  ema[0] = arr[0];
  for (let i = 1; i < arr.length; i++) {
    ema[i] = alpha * arr[i] + (1.0 - alpha) * ema[i - 1];
  }
  return ema;
}

function isInSession(tEpoch, startStr, endStr) {
  if (!startStr || !endStr || (startStr === "00:00" && (endStr === "23:59" || endStr === "24:00"))) {
    return true;
  }
  try {
    const d = new Date(tEpoch * 1000);
    const curMin = d.getUTCHours() * 60 + d.getUTCMinutes();
    const [sH, sM] = startStr.split(":").map(Number);
    const [eH, eM] = endStr.split(":").map(Number);
    const sMin = sH * 60 + sM;
    const eMin = eH * 60 + eM;
    if (sMin <= eMin) {
      return curMin >= sMin && curMin <= eMin;
    } else {
      return curMin >= sMin || curMin <= eMin;
    }
  } catch (e) {
    return true;
  }
}


function mergeSweepResults(resW, resO, bars, params) {
  const sigW = resW.all_signals || resW.signals || [];
  const sigO = resO.all_signals || resO.signals || [];

  const dedup = new Map();
  for (let i = 0; i < sigW.length; i++) {
    const t = sigW[i];
    const key = `${t.bar_index}_${t.action}`;
    dedup.set(key, { ...t });
  }

  for (let i = 0; i < sigO.length; i++) {
    const t = sigO[i];
    const key = `${t.bar_index}_${t.action}`;
    if (!dedup.has(key)) {
      dedup.set(key, { ...t });
    }
  }

  const completedTrades = Array.from(dedup.values()).sort((a, b) => a.bar_index - b.bar_index);

  let wins = 0;
  let losses = 0;
  let grossProfit = 0.0;
  let grossLoss = 0.0;
  let maxWinStreak = 0;
  let maxLossStreak = 0;
  let curWinStreak = 0;
  let curLossStreak = 0;

  for (let i = 0; i < completedTrades.length; i++) {
    const t = completedTrades[i];
    const st = t.status || "";
    const pnl = Number(t.pnl_pips !== undefined ? t.pnl_pips : (t.pnlPips || 0.0));
    const isWin = st.includes("WIN") || pnl > 0;
    const isLoss = st.includes("LOSS") || pnl < 0;

    if (isWin) {
      wins++;
      grossProfit += pnl;
      curWinStreak++;
      curLossStreak = 0;
      if (curWinStreak > maxWinStreak) maxWinStreak = curWinStreak;
    } else if (isLoss) {
      losses++;
      grossLoss += Math.abs(pnl);
      curLossStreak++;
      curWinStreak = 0;
      if (curLossStreak > maxLossStreak) maxLossStreak = curLossStreak;
    }
  }

  const totalClosed = wins + losses;
  const winRate = totalClosed > 0 ? Number(((wins / totalClosed) * 100.0).toFixed(1)) : 0.0;
  const profitFactor = grossLoss > 0 ? Number((grossProfit / grossLoss).toFixed(2)) : (grossProfit > 0 ? 99.0 : 0.0);
  const netPnl = Number((grossProfit - grossLoss).toFixed(1));

  let equity = 0.0;
  let peak = 0.0;
  let maxDd = 0.0;
  for (let i = 0; i < completedTrades.length; i++) {
    const t = completedTrades[i];
    if (["WIN_TP", "WIN_TP2", "WIN_TP1", "WIN_OPPOSITE", "LOSS_SL", "LOSS_OPPOSITE", "CLOSED_OPPOSITE"].includes(t.status)) {
      equity += Number(t.pnl_pips !== undefined ? t.pnl_pips : (t.pnlPips || 0.0));
      if (equity > peak) peak = equity;
      const dd = peak - equity;
      if (dd > maxDd) maxDd = dd;
    }
  }

  const backtestStats = {
    total_trades: totalClosed,
    closed_trades: totalClosed,
    total_setups: completedTrades.length,
    wins: wins,
    losses: losses,
    win_rate: winRate,
    winRate: winRate,
    profit_factor: profitFactor,
    profitFactor: profitFactor,
    net_pnl: netPnl,
    netPips: netPnl,
    max_drawdown: Number(maxDd.toFixed(1)),
    maxDrawdown: Number(maxDd.toFixed(1)),
    gross_profit: Number(grossProfit.toFixed(1)),
    gross_loss: Number(grossLoss.toFixed(1)),
    max_win_streak: maxWinStreak,
    max_loss_streak: maxLossStreak,
    streaks: { max_win_streak: maxWinStreak, max_loss_streak: maxLossStreak }
  };

  const computeWindowStats = (trList) => {
    const finished = trList.filter((t) => [
      "WIN_TP", "WIN_TP1", "WIN_TP2", "WIN_OPPOSITE",
      "LOSS_SL", "LOSS_OPPOSITE", "CLOSED_OPPOSITE", "BE_WIN", "BE_OPPOSITE"
    ].includes(t.status));

    const total = finished.length;
    if (total === 0) {
      return {
        trades: 0, total_trades: 0, closed_trades: 0,
        wins: 0, losses: 0, win_rate: 0.0, winRate: 0.0,
        net_pnl: 0.0, netPips: 0.0, profit_factor: 1.0, profitFactor: 1.0,
        max_win_streak: 0, max_loss_streak: 0,
        streaks: { max_win_streak: 0, max_loss_streak: 0 }
      };
    }

    let wWins = 0;
    let wLosses = 0;
    let gp = 0.0;
    let gl = 0.0;
    let wWinStreak = 0;
    let wLossStreak = 0;
    let curW = 0;
    let curL = 0;

    for (let i = 0; i < total; i++) {
      const pnl = Number(finished[i].pnl_pips !== undefined ? finished[i].pnl_pips : (finished[i].pnlPips || 0.0));
      const isW = (finished[i].status && finished[i].status.includes("WIN")) || pnl > 0;
      if (isW) {
        wWins++;
        gp += pnl;
        curW++;
        curL = 0;
        if (curW > wWinStreak) wWinStreak = curW;
      } else {
        wLosses++;
        gl += Math.abs(pnl);
        curL++;
        curW = 0;
        if (curL > wLossStreak) wLossStreak = curL;
      }
    }

    const wr = total > 0 ? Number(((wWins / total) * 100.0).toFixed(1)) : 0.0;
    const pf = gl > 0 ? Number((gp / gl).toFixed(2)) : (gp > 0 ? 99.0 : 0.0);
    const net = Number((gp - gl).toFixed(1));

    return {
      trades: total, total_trades: total, closed_trades: total,
      wins: wWins, losses: wLosses, winRate: wr, win_rate: wr,
      profitFactor: pf, profit_factor: pf, netPips: net, net_pnl: net,
      max_win_streak: wWinStreak, max_loss_streak: wLossStreak,
      streaks: { max_win_streak: wWinStreak, max_loss_streak: wLossStreak }
    };
  };

  const n = bars.length;
  const nowMs = Date.now();
  const latestBarTs = Number(bars[n - 1].time);
  let nowVnDate;
  if (Math.abs(Math.floor(nowMs / 1000) - latestBarTs) <= 4 * 86400) {
    nowVnDate = new Date(nowMs + 7 * 3600 * 1000);
  } else {
    nowVnDate = new Date(latestBarTs * 1000 + 7 * 3600 * 1000);
  }

  const vnYear = nowVnDate.getUTCFullYear();
  const vnMonth = nowVnDate.getUTCMonth();
  const vnDay = nowVnDate.getUTCDate();
  const todayStartTs = Math.floor(Date.UTC(vnYear, vnMonth, vnDay, 0, 0, 0) / 1000) - 7 * 3600;
  const threeDaysStartTs = todayStartTs - 2 * 24 * 3600;
  const sevenDaysStartTs = todayStartTs - 6 * 24 * 3600;
  const thirtyDaysStartTs = todayStartTs - 29 * 24 * 3600;

  const tradesToday = completedTrades.filter((t) => t.time >= todayStartTs);
  const trades3d = completedTrades.filter((t) => t.time >= threeDaysStartTs);
  const trades7d = completedTrades.filter((t) => t.time >= sevenDaysStartTs);
  const trades30d = completedTrades.filter((t) => t.time >= thirtyDaysStartTs);

  const backtestWindows = {
    today: computeWindowStats(tradesToday),
    "24h": computeWindowStats(tradesToday),
    "3d": computeWindowStats(trades3d),
    "7d": computeWindowStats(trades7d),
    "30d": computeWindowStats(trades30d),
    all: computeWindowStats(completedTrades),
  };

  const rawBoxes = [...(resW.sweep_boxes || []), ...(resO.sweep_boxes || [])];
  const seenBoxes = new Set();
  const uniqBoxes = [];
  for (let i = 0; i < rawBoxes.length; i++) {
    const b = rawBoxes[i];
    const k = `${b.left_time}_${b.direction}_${b.top}_${b.bottom}`;
    if (!seenBoxes.has(k)) {
      seenBoxes.add(k);
      uniqBoxes.push(b);
    }
  }

  const rawMarkers = [...(resW.chart_markers || []), ...(resO.chart_markers || [])];
  const seenMarkers = new Set();
  const uniqMarkers = [];
  for (let i = 0; i < rawMarkers.length; i++) {
    const m = rawMarkers[i];
    const k = `${m.time}_${m.position}`;
    if (!seenMarkers.has(k)) {
      seenMarkers.add(k);
      uniqMarkers.push(m);
    }
  }
  uniqMarkers.sort((a, b) => a.time - b.time);

  const rawLines = [...(resW.visual_lines || []), ...(resO.visual_lines || [])];
  const seenLines = new Set();
  const uniqLines = [];
  for (let i = 0; i < rawLines.length; i++) {
    const l = rawLines[i];
    const k = `${l.x1}_${l.x2}_${l.y1}_${l.y2}`;
    if (!seenLines.has(k)) {
      seenLines.add(k);
      uniqLines.push(l);
    }
  }

  const latestSignal = completedTrades.length > 0 ? completedTrades[completedTrades.length - 1] : null;

  return {
    signals: completedTrades.slice(-50),
    recent_signals: completedTrades.slice(-50),
    all_signals: completedTrades,
    latest_signal: latestSignal,
    chart_markers: uniqMarkers.slice(-100),
    visual_lines: uniqLines.slice(-200),
    sweep_boxes: uniqBoxes,
    liquidity_levels: resW.liquidity_levels || [],
    market_bias: resW.market_bias || "NEUTRAL",
    streaks: {
      max_win_streak: maxWinStreak,
      max_loss_streak: maxLossStreak,
    },
    backtest: backtestStats,
    backtest_windows: backtestWindows,
    backtestWindows: backtestWindows,
  };
}

export function calculateLiquiditySweep(bars, params = {}) {
  if (!bars || bars.length < 20) {
    return {
      signals: [],
      recent_signals: [],
      all_signals: [],
      latest_signal: null,
      chart_markers: [],
      visual_lines: [],
      sweep_boxes: [],
      liquidity_levels: [],
      market_bias: "NEUTRAL",
      streaks: { max_win_streak: 0, max_loss_streak: 0 },
      backtest: { total_trades: 0, wins: 0, losses: 0, win_rate: 0, profit_factor: 0, net_pnl: 0, max_drawdown: 0 },
      backtest_windows: {},
    };
  }

  // Auto M1 -> M5 Multi-Timeframe Structure Mapping:
  // Trên khung M1, đỉnh đáy nhỏ quét liên tục tạo nhiễu và dễ fail.
  // Khi chạy trên M1, hệ thống tự động resample M1 sang M5 để lấy toàn bộ
  // cấu trúc Swing High/Low & Setup chuẩn xác của M5 và chiếu đồng bộ lên M1.
  const m1AnchorM5 = params.sweepM1AnchorM5 !== false && params.sweep_m1_anchor_m5 !== false;
  if (m1AnchorM5 && !params._isResampledFromM1) {
    let isM1 = false;
    const tfHint = String(params.timeframe || params.tf || "").toUpperCase();
    if (tfHint === "M1") {
      isM1 = true;
    } else if (bars.length >= 5) {
      const diffs = [];
      for (let i = 1; i < Math.min(20, bars.length); i++) {
        const d = Number(bars[i].time) - Number(bars[i - 1].time);
        if (d > 0 && d < 3600) diffs.push(d);
      }
      if (diffs.length > 0) {
        diffs.sort((a, b) => a - b);
        const medianDiff = diffs[Math.floor(diffs.length / 2)];
        if (medianDiff <= 120) {
          isM1 = true;
        }
      }
    }

    if (isM1) {
      const m1Times = new Set(bars.map((b) => Number(b.time)));
      const m5Bars = [];
      let curBucket = -1;
      let curM5 = null;

      for (let i = 0; i < bars.length; i++) {
        const b = bars[i];
        const bTime = Number(b.time);
        const bucket = Math.floor(bTime / 300) * 300;
        const bOpen = Number(b.open);
        const bHigh = Number(b.high);
        const bLow = Number(b.low);
        const bClose = Number(b.close);
        const bVol = Number(b.volume || 0);

        if (bucket !== curBucket) {
          curBucket = bucket;
          if (curM5) m5Bars.push(curM5);
          const chosenTime = m1Times.has(bucket) ? bucket : bTime;
          curM5 = {
            time: chosenTime,
            open: bOpen,
            high: bHigh,
            low: bLow,
            close: bClose,
            volume: bVol,
          };
        } else {
          if (bHigh > curM5.high) curM5.high = bHigh;
          if (bLow < curM5.low) curM5.low = bLow;
          curM5.close = bClose;
          curM5.volume += bVol;
        }
      }
      if (curM5) m5Bars.push(curM5);

      if (m5Bars.length >= 20) {
        const resM5 = calculateLiquiditySweep(m5Bars, {
          ...params,
          timeframe: "M5",
          _isResampledFromM1: true,
        });

        // Extend right_time of active non-broken boxes to the newest M1 bar timestamp
        const latestM1Time = Number(bars[bars.length - 1].time);
        if (resM5.sweep_boxes && resM5.sweep_boxes.length > 0) {
          resM5.sweep_boxes.forEach((bx) => {
            if (!bx.broken && bx.right_time) {
              bx.right_time = Math.max(bx.right_time, latestM1Time);
            }
          });
        }
        return resM5;
      }
    }
  }

  const length = Math.max(1, Math.min(Number(params.sweepLookback || params.sweep_lookback || params.len || 5), 50));
  // Pure Breakout Strategy (Chỉ đánh phá vỡ cản & Retest, loại bỏ hoàn toàn quét râu nến)
  const opt = "Only Outbreaks & Retest";

  const extendBoxes = params.sweepExtend !== undefined ? !!params.sweepExtend : (params.sweep_extend !== undefined ? !!params.sweep_extend : (params.extend !== undefined ? !!params.extend : true));
  const maxB = Math.max(1, Math.min(Number(params.sweepMaxBars || params.sweep_max_bars || params.maxB || 300), 5000));

  let entryMode = String(params.sweepEntryMode || params.sweep_entry_mode || params.entryMode || "SMART_HYBRID").toUpperCase();
  if (entryMode.includes("HYBRID") || entryMode.includes("SMART")) {
    entryMode = "SMART_HYBRID";
  } else if (entryMode.includes("RETEST") || entryMode.includes("LIMIT")) {
    entryMode = "RETEST";
  } else {
    entryMode = "MARKET";
  }

  const zoneLimitEnable = params.sweepZoneLimitEnable !== undefined ? Boolean(params.sweepZoneLimitEnable) : (params.sweep_zone_limit_enable !== undefined ? Boolean(params.sweep_zone_limit_enable) : true);
  const hybridThresholdPips = Math.max(5.0, Math.min(Number(params.sweepHybridThresholdPips !== undefined ? params.sweepHybridThresholdPips : (params.sweep_hybrid_threshold_pips !== undefined ? params.sweep_hybrid_threshold_pips : 80.0)), 500.0));

  let hybridLimitMode = String(params.sweepHybridLimitMode || params.sweep_hybrid_limit_mode || params.hybrid_limit_mode || "RETEST").toUpperCase();
  if (hybridLimitMode.includes("40") && hybridLimitMode.includes("ZONE")) {
    hybridLimitMode = "ZONE_40";
  } else if (hybridLimitMode.includes("PCT") && hybridLimitMode.includes("ZONE")) {
    hybridLimitMode = "ZONE_PCT";
  } else if (hybridLimitMode.includes("ZONE")) {
    hybridLimitMode = "ZONE_50";
  } else if (hybridLimitMode.includes("50") || hybridLimitMode.includes("PULLBACK_50")) {
    hybridLimitMode = "PULLBACK_50";
  } else if (hybridLimitMode.includes("PCT")) {
    hybridLimitMode = "PULLBACK_PCT";
  } else if (hybridLimitMode.includes("RETEST")) {
    hybridLimitMode = "RETEST";
  } else {
    hybridLimitMode = "RETEST";
  }

  const hybridZonePct = Math.max(10.0, Math.min(Number(params.sweepHybridZonePct !== undefined ? params.sweepHybridZonePct : (params.sweep_hybrid_zone_pct !== undefined ? params.sweep_hybrid_zone_pct : 50.0)), 90.0));
  const hybridPullbackPct = Math.max(10.0, Math.min(Number(params.sweepHybridPullbackPct !== undefined ? params.sweepHybridPullbackPct : (params.sweep_hybrid_pullback_pct !== undefined ? params.sweep_hybrid_pullback_pct : 50.0)), 90.0));

  const rr = Math.max(0.5, Math.min(Number(params.sweepRr || params.sweep_rr || params.rr || 2.0), 10.0));
  const dynamicBeEnable = Boolean(params.sweepDynamicBeEnable ?? params.sweep_dynamic_be_enable ?? params.dynamicBeEnable ?? false);
  const beTriggerMode = String(params.sweepBeTriggerMode || params.sweep_be_trigger_mode || params.beTriggerMode || "PCT_TP").toUpperCase();
  const beTriggerPct = Number(params.sweepBeTriggerPct ?? params.sweep_be_trigger_pct ?? params.beTriggerPct ?? 50.0);
  const beTriggerPips = Number(params.sweepBeTriggerPips ?? params.sweep_be_trigger_pips ?? params.beTriggerPips ?? 40.0);
  const beLockPips = Number(params.sweepBeLockPips ?? params.sweep_be_lock_pips ?? params.beLockPips ?? 2.0);
  const slBuf = Math.max(0.0, Math.min(Number(params.sweepSlBufferPips !== undefined ? params.sweepSlBufferPips : (params.sweep_sl_buffer_pips !== undefined ? params.sweep_sl_buffer_pips : (params.sl_buffer_pips !== undefined ? params.sl_buffer_pips : 1.5))), 50.0));
  const minRisk = Math.max(0.5, Number(params.sweepMinRiskPips || params.sweep_min_risk_pips || params.min_risk_pips || 3.0));
  const maxRisk = Math.max(minRisk, Number(params.sweepMaxRiskPips || params.sweep_max_risk_pips || params.max_risk_pips || 150.0));
  const cooldown = Math.max(1, Number(params.sweepCooldownBars || params.sweep_cooldown_bars || params.cooldown_bars || 3));
  const minSwingPips = Number(params.sweepMinSwingPips !== undefined ? params.sweepMinSwingPips : (params.sweep_min_swing_pips !== undefined ? params.sweep_min_swing_pips : (params.min_swing_pips !== undefined ? params.min_swing_pips : 0.0)));
  const compareBars = Math.max(10, Number(params.sweepCompareBars || params.sweep_compare_bars || params.compare_bars || 25));
  const minBreakPips = Number(params.sweepMinBreakPips || params.sweep_min_break_pips || params.min_break_pips || 1.5);
  const maxWaitBars = Math.max(2, Math.min(30, Number(params.sweepMaxWaitBars || params.sweep_max_wait_bars || params.max_wait_bars || 20)));
  const usePeakFilter = params.sweepHighestPeakFilter !== undefined ? !!params.sweepHighestPeakFilter : (params.sweep_highest_peak_filter !== undefined ? !!params.sweep_highest_peak_filter : false);
  const peakLookback = Math.max(5, Number(params.sweepPeakLookback || params.sweep_peak_lookback || params.peak_lookback || 30));

  const useTrend = params.sweepTrendFilterEnable !== undefined ? !!params.sweepTrendFilterEnable : (params.sweep_trend_filter_enable !== undefined ? !!params.sweep_trend_filter_enable : (params.trend_filter_enable !== undefined ? !!params.trend_filter_enable : false));
  const emaPeriod = Math.max(10, Number(params.sweepTrendEmaPeriod || params.sweep_trend_ema_period || params.trend_ema_period || 200));

  const useSession = params.sweepSessionFilterEnable !== undefined ? !!params.sweepSessionFilterEnable : (params.sweep_session_filter_enable !== undefined ? !!params.sweep_session_filter_enable : (params.session_filter_enable !== undefined ? !!params.session_filter_enable : false));
  const sessStart = params.sweepSessionStart || params.sweep_session_start || params.session_start || "00:00";
  const sessEnd = params.sweepSessionEnd || params.sweep_session_end || params.session_end || "23:59";
  const cancelBars = Math.max(5, Number(params.sweepLimitCancelBars || params.sweep_limit_cancel_bars || params.cancelBars || 30));
  const closeOnOpposite = Boolean(params.sweepCloseOnOpposite !== undefined ? params.sweepCloseOnOpposite : (params.sweep_close_on_opposite !== undefined ? params.sweep_close_on_opposite : (params.close_on_opposite !== undefined ? params.close_on_opposite : false)));

  const n = bars.length;
  const times = new Float64Array(n);
  const opens = new Float64Array(n);
  const highs = new Float64Array(n);
  const lows = new Float64Array(n);
  const closes = new Float64Array(n);

  for (let i = 0; i < n; i++) {
    times[i] = Number(bars[i].time);
    opens[i] = Number(bars[i].open);
    highs[i] = Number(bars[i].high);
    lows[i] = Number(bars[i].low);
    closes[i] = Number(bars[i].close);
  }

  const currentPrice = closes[n - 1];
  const pipMult = getPipMultiplier(currentPrice);
  const decimals = getPriceDecimals(currentPrice);

  const ema = useTrend ? calcEma(closes, emaPeriod) : null;

  const oW = (opt === "Only Wicks");
  const oO = (opt === "Only Outbreaks & Retest");
  const WO = (opt === "Wicks + Outbreaks & Retest");

  const aPivH = [];
  const aPivL = [];
  const recentTradedPivotsL = []; // array of { prc, bix, sl }
  const recentTradedPivotsH = []; // array of { prc, bix, sl }
  const activeBuyTrades = []; // array of { entry, sl, tp, barIdx, filled }
  const activeSellTrades = [];
  let lastBuySlHitBar = -999;
  let lastSellSlHitBar = -999;
  const rawBoxes = [];
  const boxesMap = new Map();
  let activeBoxes = [];
  const visualLines = [];
  const chartMarkers = [];
  const rawTrades = [];

  let lastTradeBar = -999;

  for (let i = length * 2; i < n; i++) {
    const pivIdx = i - length;

    // Check active trades for SL hit (Rule: Dính SL thì không đánh tiếp cùng chiều trong cùng sóng xả)
    const curL_check = lows[i];
    const curH_check = highs[i];
    for (let btIdx = activeBuyTrades.length - 1; btIdx >= 0; btIdx--) {
      const bt = activeBuyTrades[btIdx];
      if (curL_check <= bt.sl) {
        lastBuySlHitBar = i;
        activeBuyTrades.splice(btIdx, 1);
      } else if (curH_check >= bt.tp) {
        activeBuyTrades.splice(btIdx, 1);
      } else if (i - bt.barIdx > cancelBars && !bt.filled) {
        activeBuyTrades.splice(btIdx, 1);
      } else if (!bt.filled && curL_check <= bt.entry) {
        bt.filled = true;
      }
    }

    for (let stIdx = activeSellTrades.length - 1; stIdx >= 0; stIdx--) {
      const st = activeSellTrades[stIdx];
      if (curH_check >= st.sl) {
        lastSellSlHitBar = i;
        activeSellTrades.splice(stIdx, 1);
      } else if (curL_check <= st.tp) {
        activeSellTrades.splice(stIdx, 1);
      } else if (i - st.barIdx > cancelBars && !st.filled) {
        activeSellTrades.splice(stIdx, 1);
      } else if (!st.filled && curH_check >= st.entry) {
        st.filled = true;
      }
    }



    // 1. Pivot High detection: ta.pivothigh(len, len) with V-Shape & Highest Peak Comparison
    let isPh = true;
    const pValH = highs[pivIdx];
    for (let j = pivIdx - length; j <= pivIdx + length; j++) {
      if (j === pivIdx) continue;
      if (j < pivIdx) {
        if (highs[j] >= pValH) {
          isPh = false;
          break;
        }
      } else {
        if (highs[j] > pValH) {
          isPh = false;
          break;
        }
      }
    }
    if (isPh) {
      // Sóng thoát ly đỉnh (Progressive Closes Drop - loại bỏ đỉnh đi ngang bẹp dí):
      if (length >= 3) {
        const c1 = closes[pivIdx + 1];
        const c2 = closes[pivIdx + 2];
        let hasLowerAfterC2 = false;
        for (let k = 3; k <= length; k++) {
          if (closes[pivIdx + k] < c2) {
            hasLowerAfterC2 = true;
            break;
          }
        }
        let descClosesCount = 0;
        for (let k = 1; k < length; k++) {
          if (closes[pivIdx + k + 1] < closes[pivIdx + k]) descClosesCount++;
        }
        const isProgressiveDrop = (c2 < c1 && hasLowerAfterC2) || (descClosesCount >= 2 && closes[pivIdx + length] < closes[pivIdx]);
        if (!isProgressiveDrop) {
          isPh = false;
        }
      }
    }

    if (isPh) {
      // Sóng thoát ly đỉnh (Progressive Closes Drop - loại bỏ đỉnh đi ngang bẹp dí):
      if (length >= 3) {
        const c1 = closes[pivIdx + 1];
        const c2 = closes[pivIdx + 2];
        let hasLowerAfterC2 = false;
        for (let k = 3; k <= length; k++) {
          if (closes[pivIdx + k] < c2) {
            hasLowerAfterC2 = true;
            break;
          }
        }
        let descClosesCount = 0;
        for (let k = 1; k < length; k++) {
          if (closes[pivIdx + k + 1] < closes[pivIdx + k]) descClosesCount++;
        }
        const isProgressiveDrop = (c2 < c1 && hasLowerAfterC2) || (descClosesCount >= 2 && closes[pivIdx + length] < closes[pivIdx]);
        if (!isProgressiveDrop) {
          isPh = false;
        }
      }
    }

    if (isPh) {
      let minLeft = pValH;
      for (let k = pivIdx - length; k < pivIdx; k++) {
        if (lows[k] < minLeft) minLeft = lows[k];
      }
      let minRight = pValH;
      for (let k = pivIdx + 1; k <= pivIdx + length; k++) {
        if (lows[k] < minRight) minRight = lows[k];
      }
      const leftDrop = (pValH - minLeft) * pipMult;
      const rightDrop = (pValH - minRight) * pipMult;

      if (leftDrop >= minSwingPips && rightDrop >= minSwingPips) {
        let passedPeakFilter = true;
        if (usePeakFilter) {
          const startLb = Math.max(0, pivIdx - peakLookback);
          for (let k = startLb; k < pivIdx; k++) {
            if (highs[k] > pValH) {
              passedPeakFilter = false;
              break;
            }
          }
        }

        if (passedPeakFilter) {
          // Rule 1: Structural Higher High invalidates older lower active peaks (they were breached/swept)
          // Rule 2: Cluster deduping: if existing active peak is within minSwingPips or compareBars, keep only the highest
          let isHighest = true;
          for (let idxP = aPivH.length - 1; idxP >= 0; idxP--) {
            const p = aPivH[idxP];
            if (p.state === 0) {
              const pipsDiff = Math.abs(p.prc - pValH) * pipMult;
              if (p.prc <= pValH) {
                // Older peak is lower -> it was breached by the move forming this new higher peak!
                aPivH.splice(idxP, 1);
              } else if (pipsDiff < minSwingPips || (pivIdx - p.bix < compareBars)) {
                // Existing peak is higher and nearby -> keep existing higher peak, skip this lower one
                isHighest = false;
                break;
              }
            }
          }
          if (isHighest) {
            for (let r = recentTradedPivotsH.length - 1; r >= 0; r--) {
              const rp = recentTradedPivotsH[r];
              if (pivIdx - rp.bix > maxB) {
                recentTradedPivotsH.splice(r, 1);
                continue;
              }
              const pipsDiff = Math.abs(rp.prc - pValH) * pipMult;
              if (Math.abs(pivIdx - rp.bix) < compareBars || pipsDiff < minSwingPips) {
                isHighest = false;
                break;
              }
            }
          }

          if (isHighest) {
            for (let r = recentTradedPivotsH.length - 1; r >= 0; r--) {
              const rp = recentTradedPivotsH[r];
              if (pivIdx - rp.bix > maxB) {
                recentTradedPivotsH.splice(r, 1);
                continue;
              }
              const pipsDiff = Math.abs(rp.prc - pValH) * pipMult;
              if (Math.abs(pivIdx - rp.bix) < compareBars || pipsDiff < minSwingPips) {
                isHighest = false;
                break;
              }
            }
          }

          if (isHighest) {
            aPivH.unshift({
              prc: pValH,
              bix: pivIdx,
              time: times[pivIdx],
              state: 0, // 0=IDLE, 1=SWEPT, 2=KILLED_NGUOC, 3=CONFIRMED, 4=INVALIDATED
              sweep_bar: -1,
              sweep_close: 0,
              sweep_extreme: pValH,
              wait_bars: 0,
              is_wick: false,
            });
          }
        }
      }
    }

    // 2. Pivot Low detection: ta.pivotlow(len, len) with V-Shape & Lowest Bottom Comparison
    let isPl = true;
    const pValL = lows[pivIdx];
    for (let j = pivIdx - length; j <= pivIdx + length; j++) {
      if (j === pivIdx) continue;
      if (j < pivIdx) {
        if (lows[j] <= pValL) {
          isPl = false;
          break;
        }
      } else {
        if (lows[j] < pValL) {
          isPl = false;
          break;
        }
      }
    }
    if (isPl) {
      // Sóng thoát ly đáy (Progressive Closes Rise - loại bỏ đáy đi ngang bẹp dí):
      if (length >= 3) {
        const c1 = closes[pivIdx + 1];
        const c2 = closes[pivIdx + 2];
        let hasHigherAfterC2 = false;
        for (let k = 3; k <= length; k++) {
          if (closes[pivIdx + k] > c2) {
            hasHigherAfterC2 = true;
            break;
          }
        }
        let ascClosesCount = 0;
        for (let k = 1; k < length; k++) {
          if (closes[pivIdx + k + 1] > closes[pivIdx + k]) ascClosesCount++;
        }
        const isProgressiveRise = (c2 > c1 && hasHigherAfterC2) || (ascClosesCount >= 2 && closes[pivIdx + length] > closes[pivIdx]);
        if (!isProgressiveRise) {
          isPl = false;
        }
      }
    }

    if (isPl) {
      // Sóng thoát ly đáy (Progressive Closes Rise - loại bỏ đáy đi ngang bẹp dí):
      if (length >= 3) {
        const c1 = closes[pivIdx + 1];
        const c2 = closes[pivIdx + 2];
        let hasHigherAfterC2 = false;
        for (let k = 3; k <= length; k++) {
          if (closes[pivIdx + k] > c2) {
            hasHigherAfterC2 = true;
            break;
          }
        }
        let ascClosesCount = 0;
        for (let k = 1; k < length; k++) {
          if (closes[pivIdx + k + 1] > closes[pivIdx + k]) ascClosesCount++;
        }
        const isProgressiveRise = (c2 > c1 && hasHigherAfterC2) || (ascClosesCount >= 2 && closes[pivIdx + length] > closes[pivIdx]);
        if (!isProgressiveRise) {
          isPl = false;
        }
      }
    }

    if (isPl) {
      let maxLeft = pValL;
      for (let k = pivIdx - length; k < pivIdx; k++) {
        if (highs[k] > maxLeft) maxLeft = highs[k];
      }
      let maxRight = pValL;
      for (let k = pivIdx + 1; k <= pivIdx + length; k++) {
        if (highs[k] > maxRight) maxRight = highs[k];
      }
      const leftRise = (maxLeft - pValL) * pipMult;
      const rightRise = (maxRight - pValL) * pipMult;

      if (leftRise >= minSwingPips && rightRise >= minSwingPips) {
        let passedBottomFilter = true;
        if (usePeakFilter) {
          const startLb = Math.max(0, pivIdx - peakLookback);
          for (let k = startLb; k < pivIdx; k++) {
            if (lows[k] < pValL) {
              passedBottomFilter = false;
              break;
            }
          }
        }

        if (passedBottomFilter) {
          // Rule 1: Structural Lower Low invalidates older higher active bottoms (they were breached/swept)
          // Rule 2: Cluster deduping: if existing active bottom is within minSwingPips or compareBars, keep only the lowest
          let isLowest = true;
          for (let idxP = aPivL.length - 1; idxP >= 0; idxP--) {
            const p = aPivL[idxP];
            if (p.state === 0) {
              const pipsDiff = Math.abs(p.prc - pValL) * pipMult;
              if (p.prc >= pValL) {
                // Older bottom is higher -> it was breached by the move forming this new lower bottom!
                aPivL.splice(idxP, 1);
              } else if (pipsDiff < minSwingPips || (pivIdx - p.bix < compareBars)) {
                // Existing bottom is lower and nearby -> keep existing lower bottom, skip this higher one
                isLowest = false;
                break;
              }
            }
          }
          if (isLowest) {
            for (let r = recentTradedPivotsL.length - 1; r >= 0; r--) {
              const rp = recentTradedPivotsL[r];
              if (pivIdx - rp.bix > maxB) {
                recentTradedPivotsL.splice(r, 1);
                continue;
              }
              const pipsDiff = Math.abs(rp.prc - pValL) * pipMult;
              if (Math.abs(pivIdx - rp.bix) < compareBars || pipsDiff < minSwingPips) {
                isLowest = false;
                break;
              }
            }
          }

          if (isLowest) {
            for (let r = recentTradedPivotsL.length - 1; r >= 0; r--) {
              const rp = recentTradedPivotsL[r];
              if (pivIdx - rp.bix > maxB) {
                recentTradedPivotsL.splice(r, 1);
                continue;
              }
              const pipsDiff = Math.abs(rp.prc - pValL) * pipMult;
              if (Math.abs(pivIdx - rp.bix) < compareBars || pipsDiff < minSwingPips) {
                isLowest = false;
                break;
              }
            }
          }

          if (isLowest) {
            aPivL.unshift({
              prc: pValL,
              bix: pivIdx,
              time: times[pivIdx],
              state: 0, // 0=IDLE, 1=SWEPT, 2=KILLED_NGUOC, 3=CONFIRMED, 4=INVALIDATED
              sweep_bar: -1,
            sweep_close: 0,
            sweep_extreme: pValL,
            wait_bars: 0,
            is_wick: false,
          });
        }
      }
    }
  }

    const curOpen = opens[i];
    const curClose = closes[i];
    const curHigh = highs[i];
    const curLow = lows[i];
    const curTime = times[i];

    const inSession = useSession ? isInSession(curTime, sessStart, sessEnd) : true;

    // 3. Process aPivH (Pivot Highs - SELL Setup: Sweep High -> Kill Nguoc -> Close below)
    const delH = [];
    let candleSweptH = false;
    for (let idx = aPivH.length - 1; idx >= 0; idx--) {
      const get = aPivH[idx];
      // Expire old zombie pivots older than maxB bars
      if (get.state === 0 && (i - get.bix > maxB)) {
        delH.push(idx);
        continue;
      }
      // Expire old zombie pivots older than maxB bars
      if (get.state === 0 && (i - get.bix > maxB)) {
        delH.push(idx);
        continue;
      }
      if (get.state === 0) {
        const prevClose = i > 0 ? closes[i - 1] : curClose;
        const prevHigh = i > 0 ? highs[i - 1] : curHigh;
        let isBreakout = (prevClose <= get.prc) && (curClose >= get.prc + minBreakPips / pipMult);
        // User Rule: Nếu nến trước đã có râu quét qua đỉnh, thì nến phá lên (breakout) bắt buộc phải tạo đỉnh cao hơn nến quét
        if (isBreakout && prevHigh > get.prc && curHigh <= prevHigh) {
          isBreakout = false;
        }
        const isWick = (prevClose <= get.prc) && (curHigh >= get.prc + minBreakPips / pipMult) && (curClose <= get.prc);
        const canBreakout = (oO || WO) && isBreakout;
        const canWick = (oW || WO) && isWick;

        if (canBreakout || canWick) {
          get.state = 1;
          get.sweep_bar = i;
          get.sweep_close = curClose;
          get.sweep_high = curHigh;
          get.sweep_low = curLow;
          const effHigh = prevHigh > get.prc ? Math.max(curHigh, prevHigh) : curHigh;
          get.sweep_extreme = effHigh;
          get.wait_bars = 0;
          get.is_wick = canWick;

          if (canWick) {
            get.zone_top = effHigh;
            get.zone_bottom = get.prc;
          } else {
            get.zone_top = Math.max(get.prc, effHigh);
            get.zone_bottom = Math.min(get.prc, curLow);
          }

          // Visual Box immediately covers the sweep zone
          const _bx = {
            direction: 1,
            left_bix: get.sweep_bar,
            left_time: times[get.sweep_bar],
            right_bix: i,
            right_time: curTime,
            top: Number(get.zone_top.toFixed(decimals)),
            bottom: Number(get.zone_bottom.toFixed(decimals)),
            broken: false,
            sweep_bix: get.sweep_bar,
            sweep_time: times[get.sweep_bar],
            rejection_y1: Number(get.zone_top.toFixed(decimals)),
            rejection_y2: Number(get.zone_bottom.toFixed(decimals)),
          };
          rawBoxes.push(_bx);
          boxesMap.set(`1_${get.sweep_bar}`, _bx);
          activeBoxes.push(_bx);

          // Dotted line from pivot to sweep bar
          visualLines.push({
            x1: get.time,
            x2: times[get.sweep_bar],
            y1: Number(get.prc.toFixed(decimals)),
            y2: Number(get.prc.toFixed(decimals)),
            color: "#f23645",
            style: 1, // Dotted
            width: 1.0,
          });
        }
      } else if (get.state === 1 || get.state === 2) {
        get.wait_bars += 1;
        get.sweep_extreme = Math.max(get.sweep_extreme, curHigh);

        // Invalidation: nếu có cây nến đóng trên cây nến sweep zone thì bỏ ngay lập tức, không giữ zone
        if (curClose > get.zone_top || get.wait_bars > maxWaitBars) {
          get.state = 4;
          const _bx = boxesMap.get(`1_${get.sweep_bar}`);
          if (_bx) {
            _bx.broken = true;
            _bx.right_bix = i;
            _bx.right_time = curTime;
          }
          delH.push(idx);
          continue;
        }

        // Confirmation Candle check:
        const isBearish = curClose < curOpen;
        let isConfirmed = false;
        if (get.is_wick) {
          isConfirmed = isBearish && (curClose <= get.prc);
        } else {
          isConfirmed = isBearish && (curClose < get.zone_bottom);
        }

        if (isConfirmed) {
          // SWEEP SELL CONFIRMED!
          get.state = 3;
          const _bx = boxesMap.get(`1_${get.sweep_bar}`);
          if (_bx) {
            _bx.right_bix = i;
            _bx.right_time = curTime;
          }

          // Trade Signal Generation
          if (!candleSweptH && inSession && (!useTrend || (ema && curClose <= ema[i]))) {
            candleSweptH = true;
            if (i - lastTradeBar >= cooldown) {
              const mktEntry = curClose;
              const slPrc = get.sweep_extreme + slBuf / pipMult;
              const mktRisk = (slPrc - mktEntry) * pipMult;
              const baseRetestEntry = get.zone_bottom;
              const baseRetestRisk = (slPrc - baseRetestEntry) * pipMult;

              let tradeEntryMode = "LIMIT";
              let entryPrc = baseRetestEntry;
              let risk = baseRetestRisk;
              let entryTypeDesc = "Limit Retest";

              if (entryMode === "MARKET") {
                tradeEntryMode = "MARKET";
                entryPrc = mktEntry;
                risk = mktRisk;
                entryTypeDesc = "Market Sweep";
              } else { // "RETEST" or Limit
                if (zoneLimitEnable && (baseRetestRisk > hybridThresholdPips)) {
                  const zoneSpan = Math.max(0.0, get.sweep_extreme - get.zone_bottom);
                  const zPct = (hybridZonePct || 50.0) / 100.0;
                  entryPrc = get.zone_bottom + zPct * zoneSpan;
                  risk = (slPrc - entryPrc) * pipMult;
                  entryTypeDesc = `Limit ${Math.round(hybridZonePct || 50.0)}% Zone`;
                  tradeEntryMode = "LIMIT";
                } else {
                  tradeEntryMode = "LIMIT";
                  entryPrc = baseRetestEntry;
                  risk = baseRetestRisk;
                  entryTypeDesc = "Limit Retest";
                }
              }

              if (risk >= minRisk && risk <= maxRisk) {
                const targetTp = entryPrc - (slPrc - entryPrc) * rr;
                rawTrades.push({
                  bar_index: i,
                  time: curTime,
                  action: "SELL",
                  type: "SWEEP_KILL_REVERSAL",
                  entry_mode: tradeEntryMode,
                  entry_type: entryTypeDesc,
                  piv_price: Number(get.prc.toFixed(decimals)),
                  entry: Number(entryPrc.toFixed(decimals)),
                  price: Number(entryPrc.toFixed(decimals)),
                  sl: Number(slPrc.toFixed(decimals)),
                  tp: Number(targetTp.toFixed(decimals)),
                  tp1: Number(targetTp.toFixed(decimals)),
                  tp2: Number(targetTp.toFixed(decimals)),
                  risk_pips: Number(risk.toFixed(1)),
                  market_risk_pips: Number(mktRisk.toFixed(1)),
                  sweep_high: Number(get.sweep_extreme.toFixed(decimals)),
                  sweep_low: Number(get.zone_bottom.toFixed(decimals)),
                  cancel_bars: cancelBars,
                });
                lastTradeBar = i;
                recentTradedPivotsH.push({ prc: get.prc, bix: i, sl: slPrc });
                activeSellTrades.push({ entry: entryPrc, sl: slPrc, tp: targetTp, barIdx: i, filled: false });
                chartMarkers.push({
                  time: curTime,
                  position: "aboveBar",
                  color: "#f23645",
                  shape: "arrowDown",
                  text: tradeEntryMode === "MARKET" ? "SELL" : "LIMIT SELL",
                });
              }
            }
          }
          delH.push(idx);
          continue;
        }
      }

      if (i - get.bix > 2000 || get.state === 3 || get.state === 4) {
        if (!delH.includes(idx)) {
          delH.push(idx);
        }
      }
    }

    const uniqueDelH = Array.from(new Set(delH)).sort((a, b) => b - a);
    for (let d = 0; d < uniqueDelH.length; d++) {
      aPivH.splice(uniqueDelH[d], 1);
    }

    // 4. Process aPivL (Pivot Lows - BUY Setup: Sweep Low -> Kill Nguoc -> Close above)
    const delL = [];
    let candleSweptL = false;
    for (let idx = aPivL.length - 1; idx >= 0; idx--) {
      const get = aPivL[idx];
      // Expire old zombie pivots older than maxB bars
      if (get.state === 0 && (i - get.bix > maxB)) {
        delL.push(idx);
        continue;
      }
      // Expire old zombie pivots older than maxB bars
      if (get.state === 0 && (i - get.bix > maxB)) {
        delL.push(idx);
        continue;
      }
      if (get.state === 0) {
        const prevClose = i > 0 ? closes[i - 1] : curClose;
        const prevLow = i > 0 ? lows[i - 1] : curLow;
        let isBreakout = (prevClose >= get.prc) && (curClose <= get.prc - minBreakPips / pipMult);
        // User Rule: Nếu nến trước đã có râu quét qua đáy, thì nến phá xuống (breakout) bắt buộc phải tạo đáy thấp hơn nến quét
        if (isBreakout && prevLow < get.prc && curLow >= prevLow) {
          isBreakout = false;
        }
        const isWick = (prevClose >= get.prc) && (curLow <= get.prc - minBreakPips / pipMult) && (curClose >= get.prc);
        const canBreakout = (oO || WO) && isBreakout;
        const canWick = (oW || WO) && isWick;

        if (canBreakout || canWick) {
          get.state = 1;
          get.sweep_bar = i;
          get.sweep_close = curClose;
          get.sweep_high = curHigh;
          get.sweep_low = curLow;
          const effLow = prevLow < get.prc ? Math.min(curLow, prevLow) : curLow;
          get.sweep_extreme = effLow;
          get.wait_bars = 0;
          get.is_wick = canWick;

          if (canWick) {
            get.zone_top = get.prc;
            get.zone_bottom = effLow;
          } else {
            get.zone_top = Math.max(get.prc, curHigh);
            get.zone_bottom = Math.min(get.prc, effLow);
          }

          // Visual Box immediately covers the sweep zone
          const _bx = {
            direction: -1,
            left_bix: get.sweep_bar,
            left_time: times[get.sweep_bar],
            right_bix: i,
            right_time: curTime,
            top: Number(get.zone_top.toFixed(decimals)),
            bottom: Number(get.zone_bottom.toFixed(decimals)),
            broken: false,
            sweep_bix: get.sweep_bar,
            sweep_time: times[get.sweep_bar],
            rejection_y1: Number(get.zone_top.toFixed(decimals)),
            rejection_y2: Number(get.zone_bottom.toFixed(decimals)),
          };
          rawBoxes.push(_bx);
          boxesMap.set(`-1_${get.sweep_bar}`, _bx);
          activeBoxes.push(_bx);

          // Dotted line from pivot to sweep bar
          visualLines.push({
            x1: get.time,
            x2: times[get.sweep_bar],
            y1: Number(get.prc.toFixed(decimals)),
            y2: Number(get.prc.toFixed(decimals)),
            color: "#089981",
            style: 1, // Dotted
            width: 1.0,
          });
        }
      } else if (get.state === 1 || get.state === 2) {
        get.wait_bars += 1;
        get.sweep_extreme = Math.min(get.sweep_extreme, curLow);

        // Invalidation: nếu có cây nến đóng dưới cây nến sweep zone thì bỏ ngay lập tức, không giữ zone
        if (curClose < get.zone_bottom || get.wait_bars > maxWaitBars) {
          get.state = 4;
          const _bx = boxesMap.get(`-1_${get.sweep_bar}`);
          if (_bx) {
            _bx.broken = true;
            _bx.right_bix = i;
            _bx.right_time = curTime;
          }
          delL.push(idx);
          continue;
        }

        // Confirmation Candle check:
        const isBullish = curClose > curOpen;
        let isConfirmed = false;
        if (get.is_wick) {
          isConfirmed = isBullish && (curClose >= get.prc);
        } else {
          isConfirmed = isBullish && (curClose > get.zone_top);
        }

        if (isConfirmed) {
          // SWEEP BUY CONFIRMED!
          get.state = 3;
          const _bx = boxesMap.get(`-1_${get.sweep_bar}`);
          if (_bx) {
            _bx.right_bix = i;
            _bx.right_time = curTime;
          }

          // Trade Signal Generation
          if (!candleSweptL && inSession && (!useTrend || (ema && curClose >= ema[i]))) {
            candleSweptL = true;
            if (i - lastTradeBar >= cooldown) {
              const mktEntry = curClose;
              const slPrc = get.sweep_extreme - slBuf / pipMult;
              const mktRisk = (mktEntry - slPrc) * pipMult;
              const baseRetestEntry = get.zone_top;
              const baseRetestRisk = (baseRetestEntry - slPrc) * pipMult;

              let tradeEntryMode = "LIMIT";
              let entryPrc = baseRetestEntry;
              let risk = baseRetestRisk;
              let entryTypeDesc = "Limit Retest";

              if (entryMode === "MARKET") {
                tradeEntryMode = "MARKET";
                entryPrc = mktEntry;
                risk = mktRisk;
                entryTypeDesc = "Market Sweep";
              } else { // "RETEST" or Limit
                if (zoneLimitEnable && (baseRetestRisk > hybridThresholdPips)) {
                  const zoneSpan = Math.max(0.0, get.zone_top - get.sweep_extreme);
                  const zPct = (hybridZonePct || 50.0) / 100.0;
                  entryPrc = get.zone_top - zPct * zoneSpan;
                  risk = (entryPrc - slPrc) * pipMult;
                  entryTypeDesc = `Limit ${Math.round(hybridZonePct || 50.0)}% Zone`;
                  tradeEntryMode = "LIMIT";
                } else {
                  tradeEntryMode = "LIMIT";
                  entryPrc = baseRetestEntry;
                  risk = baseRetestRisk;
                  entryTypeDesc = "Limit Retest";
                }
              }

              if (risk >= minRisk && risk <= maxRisk) {
                const targetTp = entryPrc + (entryPrc - slPrc) * rr;
                rawTrades.push({
                  bar_index: i,
                  time: curTime,
                  action: "BUY",
                  type: "SWEEP_KILL_REVERSAL",
                  entry_mode: tradeEntryMode,
                  entry_type: entryTypeDesc,
                  piv_price: Number(get.prc.toFixed(decimals)),
                  entry: Number(entryPrc.toFixed(decimals)),
                  price: Number(entryPrc.toFixed(decimals)),
                  sl: Number(slPrc.toFixed(decimals)),
                  tp: Number(targetTp.toFixed(decimals)),
                  tp1: Number(targetTp.toFixed(decimals)),
                  tp2: Number(targetTp.toFixed(decimals)),
                  risk_pips: Number(risk.toFixed(1)),
                  market_risk_pips: Number(mktRisk.toFixed(1)),
                  sweep_high: Number(get.zone_top.toFixed(decimals)),
                  sweep_low: Number(get.sweep_extreme.toFixed(decimals)),
                  cancel_bars: cancelBars,
                });
                lastTradeBar = i;
                recentTradedPivotsL.push({ prc: get.prc, bix: i, sl: slPrc });
                activeBuyTrades.push({ entry: entryPrc, sl: slPrc, tp: targetTp, barIdx: i, filled: false });
                chartMarkers.push({
                  time: curTime,
                  position: "belowBar",
                  color: "#089981",
                  shape: "arrowUp",
                  text: tradeEntryMode === "MARKET" ? "BUY" : "LIMIT BUY",
                });
              }
            }
          }
          delL.push(idx);
          continue;
        }
      }

      if (i - get.bix > 2000 || get.state === 3 || get.state === 4) {
        if (!delL.includes(idx)) {
          delL.push(idx);
        }
      }
    }

    const uniqueDelL = Array.from(new Set(delL)).sort((a, b) => b - a);
    for (let d = 0; d < uniqueDelL.length; d++) {
      aPivL.splice(uniqueDelL[d], 1);
    }

    // 5. Extend Sweep Boxes
    if (extendBoxes && activeBoxes.length > 0) {
      const stillActive = [];
      for (let b = 0; b < activeBoxes.length; b++) {
        const bx = activeBoxes[b];
        if (bx.broken || i - bx.left_bix - 1 > maxB) continue;
        bx.right_bix = i;
        bx.right_time = times[i];
        if (bx.direction === -1 && curClose < bx.bottom) {
          bx.broken = true;
        } else if (bx.direction === 1 && curClose > bx.top) {
          bx.broken = true;
        } else {
          stillActive.push(bx);
        }
      }
      activeBoxes = stillActive;
    }
  }

  // Compile visual sweep boxes:
  // Keep unbroken active zones (up to 5) + most recent trade setup zone to avoid chart clutter
  const unbrokenBoxes = rawBoxes.slice().reverse().filter((b) => !b.broken);
  const displayBoxes = unbrokenBoxes.slice(0, 5);
  if (rawBoxes.length > 0 && !displayBoxes.some((b) => b.left_bix === rawBoxes[rawBoxes.length - 1].left_bix)) {
    displayBoxes.unshift(rawBoxes[rawBoxes.length - 1]);
  }

  const sweepBoxes = displayBoxes.map((bx) => {
    const isBull = bx.direction === -1;
    return {
      direction: bx.direction,
      left_time: bx.left_time,
      right_time: bx.right_time,
      top: bx.top,
      bottom: bx.bottom,
      broken: bx.broken,
      color: isBull ? "#08998126" : "#f2364526",
      border_color: isBull ? "#08998180" : "#f2364580",
      sweep_bar_time: bx.sweep_time,
      rejection_y1: bx.rejection_y1,
      rejection_y2: bx.rejection_y2,
    };
  });

  // Do NOT export unmitigated levels to prevent dashed horizontal stripe clutter
  const liquidityLevels = [];

  // 6. Forward Trade Evaluation
  const completedTrades = [];
  let wins = 0;
  let losses = 0;
  let grossProfit = 0.0;
  let grossLoss = 0.0;
  let maxWinStreak = 0;
  let maxLossStreak = 0;
  let curWinStreak = 0;
  let curLossStreak = 0;

  // Precompute map of signal triggers per bar for closeOnOpposite
  const oppSetups = new Map();
  for (let r = 0; r < rawTrades.length; r++) {
    oppSetups.set(rawTrades[r].bar_index, rawTrades[r].action);
  }

  for (let t = 0; t < rawTrades.length; t++) {
    const trade = { ...rawTrades[t] };
    const barIdx = trade.bar_index;
    const act = trade.action;
    const entry = trade.entry;
    const sl = trade.sl;
    const tp2 = trade.tp2;
    const risk = trade.risk_pips;
    const mode = trade.entry_mode;
    const cLimit = trade.cancel_bars;

    let status = "PENDING";
    let fillBar = -1;
    let exitBar = -1;
    let exitPrice = entry;
    let pnlPips = 0.0;
    let rMult = 0.0;
    let beTriggered = false;
    let currentSl = sl;
    const beLockPrice = beLockPips / pipMult;

    if (mode === "RETEST" || mode === "LIMIT") {
      let filled = false;
      const maxFillBar = Math.min(n, barIdx + 1 + cLimit);
      for (let f = barIdx + 1; f < maxFillBar; f++) {
        // Check if opposite setup fired before limit order filled -> Cancel order
        if (closeOnOpposite && oppSetups.has(f) && oppSetups.get(f) !== act) {
          status = "CANCELED_OPPOSITE";
          break;
        }

        if (act === "BUY" && lows[f] <= entry) {
          filled = true;
          fillBar = f;
          break;
        } else if (act === "SELL" && highs[f] >= entry) {
          filled = true;
          fillBar = f;
          break;
        }
        if (act === "BUY" && lows[f] <= sl) {
          status = "CANCELED";
          break;
        } else if (act === "SELL" && highs[f] >= sl) {
          status = "CANCELED";
          break;
        }
      }

      if (status === "CANCELED" || status === "CANCELED_OPPOSITE") {
        trade.status = status;
        trade.pnl_pips = 0.0;
        trade.pnlPips = 0.0;
        trade.pnl_diff = 0.0;
        trade.rr_ratio = `1:${rr.toFixed(1)}`;
        completedTrades.push(trade);
        continue;
      }

      if (!filled) {
        if (n - 1 - barIdx >= cLimit) {
          trade.status = "EXPIRED";
          trade.pnl_pips = 0.0;
          trade.pnlPips = 0.0;
          trade.pnl_diff = 0.0;
          trade.rr_ratio = `1:${rr.toFixed(1)}`;
        } else {
          trade.status = "PENDING";
          trade.pnl_pips = 0.0;
          trade.pnlPips = 0.0;
          trade.pnl_diff = 0.0;
          trade.rr_ratio = `1:${rr.toFixed(1)}`;
        }
        completedTrades.push(trade);
        continue;
      }

      var startEval = fillBar;
    } else {
      var startEval = barIdx + 1;
    }

    status = "OPEN";
    let buyTriggerPrc = entry + (tp2 - entry) * (beTriggerPct / 100.0);
    let sellTriggerPrc = entry - (entry - tp2) * (beTriggerPct / 100.0);
    if (beTriggerMode === "PIPS") {
      buyTriggerPrc = entry + (beTriggerPips / pipMult);
      sellTriggerPrc = entry - (beTriggerPips / pipMult);
    }

    for (let k = startEval; k < n; k++) {
      if (act === "BUY") {
        // Dynamic Break-Even trigger
        if (dynamicBeEnable && !beTriggered && highs[k] >= buyTriggerPrc) {
          beTriggered = true;
          currentSl = Math.max(currentSl, entry + beLockPrice);
        }

        if (highs[k] >= tp2) {
          status = "WIN_TP";
          exitBar = k;
          exitPrice = tp2;
          pnlPips = risk * rr;
          rMult = rr;
          break;
        } else if (lows[k] <= currentSl) {
          if (beTriggered) {
            status = "BE_WIN";
            exitBar = k;
            exitPrice = currentSl;
            pnlPips = beLockPips;
            rMult = risk > 0 ? beLockPips / risk : 0.0;
          } else {
            status = "LOSS_SL";
            exitBar = k;
            exitPrice = sl;
            pnlPips = -risk;
            rMult = -1.0;
          }
          break;
        }
      } else {
        // Dynamic Break-Even trigger
        if (dynamicBeEnable && !beTriggered && lows[k] <= sellTriggerPrc) {
          beTriggered = true;
          currentSl = Math.min(currentSl, entry - beLockPrice);
        }

        if (lows[k] <= tp2) {
          status = "WIN_TP";
          exitBar = k;
          exitPrice = tp2;
          pnlPips = risk * rr;
          rMult = rr;
          break;
        } else if (highs[k] >= currentSl) {
          if (beTriggered) {
            status = "BE_WIN";
            exitBar = k;
            exitPrice = currentSl;
            pnlPips = beLockPips;
            rMult = risk > 0 ? beLockPips / risk : 0.0;
          } else {
            status = "LOSS_SL";
            exitBar = k;
            exitPrice = sl;
            pnlPips = -risk;
            rMult = -1.0;
          }
          break;
        }
      }

      // Close on Opposite Signal (Cut old trade when reverse setup appears)
      if (closeOnOpposite && oppSetups.has(k) && oppSetups.get(k) !== act) {
        exitBar = k;
        exitPrice = closes[k];
        pnlPips = act === "BUY" ? (exitPrice - entry) * pipMult : (entry - exitPrice) * pipMult;
        rMult = risk > 0 ? pnlPips / risk : 0.0;
        if (pnlPips > 0) {
          status = "WIN_OPPOSITE";
        } else if (pnlPips < 0) {
          status = "LOSS_OPPOSITE";
        } else {
          status = "BE_OPPOSITE";
        }
        break;
      }
    }

    if (status === "OPEN") {
      const unrealized = act === "BUY" ? (currentPrice - entry) * pipMult : (entry - currentPrice) * pipMult;
      trade.status = "OPEN";
      trade.pnl_pips = Number(unrealized.toFixed(1));
      trade.r_multiple = risk > 0 ? Number((unrealized / risk).toFixed(2)) : 0.0;
    } else {
      trade.status = status;
      trade.exit_bar = exitBar;
      trade.exit_time = exitBar >= 0 && exitBar < n ? times[exitBar] : times[n - 1];
      trade.exit_price = Number(exitPrice.toFixed(decimals));
      trade.pnl_pips = Number(pnlPips.toFixed(1));
      trade.pnlPips = trade.pnl_pips;
      trade.pnl_diff = Number((trade.pnl_pips / pipMult).toFixed(decimals));
      trade.rr_ratio = `1:${rr.toFixed(1)}`;
      trade.r_multiple = Number(rMult.toFixed(2));
      trade.be_triggered = beTriggered;
      const isWin = status.includes("WIN") || status === "BE_WIN" || pnlPips > 0;
      const isLoss = status.includes("LOSS") || pnlPips < 0;
      trade.win = isWin;

      if (isWin) {
        wins++;
        grossProfit += pnlPips;
        curWinStreak++;
        curLossStreak = 0;
        if (curWinStreak > maxWinStreak) maxWinStreak = curWinStreak;
      } else if (isLoss) {
        losses++;
        grossLoss += Math.abs(pnlPips);
        curLossStreak++;
        curWinStreak = 0;
        if (curLossStreak > maxLossStreak) maxLossStreak = curLossStreak;
      }
    }

    completedTrades.push(trade);
  }

  const totalClosed = wins + losses;
  const winRate = totalClosed > 0 ? Number(((wins / totalClosed) * 100.0).toFixed(1)) : 0.0;
  const profitFactor = grossLoss > 0 ? Number((grossProfit / grossLoss).toFixed(2)) : grossProfit > 0 ? 99.0 : 0.0;
  const netPnl = Number((grossProfit - grossLoss).toFixed(1));

  let equity = 0.0;
  let peak = 0.0;
  let maxDd = 0.0;
  for (let t = 0; t < completedTrades.length; t++) {
    if (["WIN_TP", "WIN_TP2", "WIN_TP1", "WIN_OPPOSITE", "LOSS_SL", "LOSS_OPPOSITE", "CLOSED_OPPOSITE"].includes(completedTrades[t].status)) {
      equity += completedTrades[t].pnl_pips;
      if (equity > peak) peak = equity;
      const dd = peak - equity;
      if (dd > maxDd) maxDd = dd;
    }
  }

  const backtestStats = {
    total_trades: totalClosed,
    closed_trades: totalClosed,
    total_setups: completedTrades.length,
    wins,
    losses,
    win_rate: winRate,
    profit_factor: profitFactor,
    net_pnl: netPnl,
    max_drawdown: Number(maxDd.toFixed(1)),
    gross_profit: Number(grossProfit.toFixed(1)),
    gross_loss: Number(grossLoss.toFixed(1)),
    max_win_streak: maxWinStreak,
    max_loss_streak: maxLossStreak,
    streaks: { max_win_streak: maxWinStreak, max_loss_streak: maxLossStreak },
  };

  const computeWindowStats = (trList) => {
    const finished = trList.filter((t) =>
      ["WIN_TP", "WIN_TP1", "WIN_TP2", "WIN_OPPOSITE", "LOSS_SL", "LOSS_OPPOSITE", "CLOSED_OPPOSITE", "BE_WIN", "BE_OPPOSITE"].includes(t.status)
    );
    const total = finished.length;
    if (total === 0) {
      return {
        trades: 0, total_trades: 0, closed_trades: 0, wins: 0, losses: 0,
        winRate: 0.0, win_rate: 0.0, profitFactor: 1.0, profit_factor: 1.0,
        netPips: 0.0, net_pnl: 0.0, max_win_streak: 0, max_loss_streak: 0,
        streaks: { max_win_streak: 0, max_loss_streak: 0 }
      };
    }
    let wWins = 0;
    let wLosses = 0;
    let gp = 0.0;
    let gl = 0.0;
    let wWinStreak = 0;
    let wLossStreak = 0;
    let curW = 0;
    let curL = 0;

    for (let i = 0; i < finished.length; i++) {
      const t = finished[i];
      const pnl = Number(t.pnl_pips || 0.0);
      const isW = (t.status && t.status.includes("WIN")) || pnl > 0;
      if (isW) {
        wWins++;
        gp += pnl;
        curW++;
        curL = 0;
        if (curW > wWinStreak) wWinStreak = curW;
      } else {
        wLosses++;
        gl += Math.abs(pnl);
        curL++;
        curW = 0;
        if (curL > wLossStreak) wLossStreak = curL;
      }
    }

    const wr = total > 0 ? Number(((wWins / total) * 100.0).toFixed(1)) : 0.0;
    const pf = gl > 0 ? Number((gp / gl).toFixed(2)) : (gp > 0 ? 99.0 : 0.0);
    const net = Number((gp - gl).toFixed(1));

    return {
      trades: total,
      total_trades: total,
      closed_trades: total,
      wins: wWins,
      losses: wLosses,
      winRate: wr,
      win_rate: wr,
      profitFactor: pf,
      profit_factor: pf,
      netPips: net,
      net_pnl: net,
      max_win_streak: wWinStreak,
      max_loss_streak: wLossStreak,
      streaks: { max_win_streak: wWinStreak, max_loss_streak: wLossStreak }
    };
  };

  const nowMs = Date.now();
  const latestBarTs = Number(times[n - 1]);
  let nowVnDate;
  // If data is recent (within 4 days, covering weekends), anchor to Real-World Today (UTC+7)
  if (Math.abs(Math.floor(nowMs / 1000) - latestBarTs) <= 4 * 86400) {
    nowVnDate = new Date(nowMs + 7 * 3600 * 1000);
  } else {
    nowVnDate = new Date(latestBarTs * 1000 + 7 * 3600 * 1000);
  }

  const vnYear = nowVnDate.getUTCFullYear();
  const vnMonth = nowVnDate.getUTCMonth();
  const vnDay = nowVnDate.getUTCDate();
  const todayStartTs = Math.floor(Date.UTC(vnYear, vnMonth, vnDay, 0, 0, 0) / 1000) - 7 * 3600;
  const threeDaysStartTs = todayStartTs - 2 * 24 * 3600;
  const sevenDaysStartTs = todayStartTs - 6 * 24 * 3600;
  const thirtyDaysStartTs = todayStartTs - 29 * 24 * 3600;

  const tradesToday = completedTrades.filter((t) => t.time >= todayStartTs);
  const trades3d = completedTrades.filter((t) => t.time >= threeDaysStartTs);
  const trades7d = completedTrades.filter((t) => t.time >= sevenDaysStartTs);
  const trades30d = completedTrades.filter((t) => t.time >= thirtyDaysStartTs);

  const backtestWindows = {
    today: computeWindowStats(tradesToday),
    "24h": computeWindowStats(tradesToday),
    "3d": computeWindowStats(trades3d),
    "7d": computeWindowStats(trades7d),
    "30d": computeWindowStats(trades30d),
    all: computeWindowStats(completedTrades),
  };

  let latestSignal = null;
  if (completedTrades.length > 0) {
    latestSignal = completedTrades[completedTrades.length - 1];
  }

  // Draw Trade Lines for Active/Pending signal so chart is clear and never frozen
  if (latestSignal && ["OPEN", "PENDING"].includes(latestSignal.status)) {
    const sigTime = latestSignal.time;
    const entryP = latestSignal.entry;
    const slP = latestSignal.sl;
    const tp2P = latestSignal.tp2;

    visualLines.push({
      x1: sigTime,
      x2: times[n - 1],
      y1: entryP,
      y2: entryP,
      color: "#8b5cf6",
      style: 0,
      width: 1.5,
      label: `Entry: ${entryP.toFixed(2)}`,
    });
    visualLines.push({
      x1: sigTime,
      x2: times[n - 1],
      y1: slP,
      y2: slP,
      color: "#ef4444",
      style: 2,
      width: 1.5,
      label: `SL: ${slP.toFixed(2)}`,
    });
    visualLines.push({
      x1: sigTime,
      x2: times[n - 1],
      y1: tp2P,
      y2: tp2P,
      color: "#10b981",
      style: 2,
      width: 1.5,
      label: `TP (${rr.toFixed(1)}R): ${tp2P.toFixed(2)}`,
    });
  }

  let bias = "NEUTRAL";
  if (closes.length >= 50) {
    if (currentPrice > closes[closes.length - 20]) bias = "BULLISH";
    else if (currentPrice < closes[closes.length - 20]) bias = "BEARISH";
  }

  return {
    signals: completedTrades.slice(-50),
    recent_signals: completedTrades.slice(-50),
    all_signals: completedTrades,
    latest_signal: latestSignal,
    chart_markers: chartMarkers.slice(-100),
    visual_lines: visualLines.slice(-200),
    sweep_boxes: sweepBoxes,
    liquidity_levels: liquidityLevels.slice(0, 40),
    market_bias: bias,
    streaks: {
      max_win_streak: maxWinStreak,
      max_loss_streak: maxLossStreak,
    },
    backtest: backtestStats,
    backtest_windows: backtestWindows,
  };
}




